import {
  BulkActionContainerCol,
  Col,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  SharedserviceMelcoModelsAlphabetCollection,
  Search,
  useTableSelection,
  TableCellLink,
} from "melco-shared-logic";
import { PublishedState } from "../../state/PublishedState";
import { BulkActionDeleteFontCollections } from "./BulkActionDeleteFontCollections";
import { useFontCollections } from "../../../hooks/libraries/fontCollections/useFontCollections";

const DETAIL_VIEW_PATH = "/libraries/font-collections/embroidery";

export const FontCollectionsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useFontCollections();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: SharedserviceMelcoModelsAlphabetCollection) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    {
      title: t("font_collections_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsAlphabetCollection) => (
        <ValueOrFallback
          value={item.name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("font_collections_list.columns.alphabet_count"),
      key: "alphabet_count",
      dataIndex: "alphabet_count",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsAlphabetCollection) => (
        <ValueOrFallback
          value={item.alphabet_count}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("font_collections_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsAlphabetCollection) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("font_collections_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsAlphabetCollection) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
    {
      title: t("font_collections_list.columns.publish_state"),
      key: "publish_state",
      dataIndex: "publish_state",
      sorter: true,
      render: (_: string, item: SharedserviceMelcoModelsAlphabetCollection) => {
        return <PublishedState publishedState={item.publish_state ?? 1} />;
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteFontCollections
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
            data={data}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("font_collections_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
